import { Component, EventEmitter, Input, Output, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatSliderModule } from '@angular/material/slider';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

interface WeekInfo {
  weekNumber: number;
  startDate: Date;
  endDate: Date;
  season: 'spring' | 'summer' | 'autumn' | 'winter';
}

@Component({
  selector: 'app-week-selector',
  standalone: true,
  imports: [
    MatSliderModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
],
  templateUrl: './week-selector.component.html',
  styleUrls: ['./week-selector.component.scss']
})
export class WeekSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showExport = true;
  @Input() exportDisabled = false;
  @Input() isExporting = false;

  @Output() weekChanged = new EventEmitter<WeekInfo>();
  @Output() exportRequested = new EventEmitter<void>();
  @ViewChild('weekSlider', { static: false }) weekSlider!: ElementRef;

  currentYear = new Date().getFullYear();
  currentWeek = 1;
  maxWeeks = 52;
  weeks: WeekInfo[] = [];

  private readonly sliderInput$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    this.generateWeeks();
    this.currentWeek = this.getCurrentWeekNumber();
    // 300ms debounce: gyors csúszka-mozgásnál csak az utolsó pozíció indít kérést
    this.sliderInput$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => this.onWeekChange());
    // Az initial emittálást microtask-ra halasztjuk, hogy elkerüljük az NG0100-t
    Promise.resolve().then(() => this.onWeekChange());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    // Ripple effekt programozottan való eltávolítása
    setTimeout(() => {
      this.removeRippleEffects();
    }, 100);
  }

  private removeRippleEffects() {
    if (this.weekSlider && this.weekSlider.nativeElement) {
      const sliderElement = this.weekSlider.nativeElement;
      
      try {
        // Ripple elemek keresése és eltávolítása
        const rippleElements = sliderElement.querySelectorAll('.mat-ripple');
        rippleElements.forEach((ripple: any) => {
          if (ripple) {
            ripple.style.display = 'none';
            ripple.style.width = '0';
            ripple.style.height = '0';
          }
        });

        const rippleElementsAll = sliderElement.querySelectorAll('.mat-ripple-element');
        rippleElementsAll.forEach((ripple: any) => {
          if (ripple) {
            ripple.style.display = 'none';
          }
        });

        const focusIndicators = sliderElement.querySelectorAll('.mat-mdc-focus-indicator');
        focusIndicators.forEach((indicator: any) => {
          if (indicator) {
            indicator.style.display = 'none';
          }
        });
      } catch (error) {
        // Csendes hiba kezelés - nem kritikus funkció
        console.debug('Ripple effects removal failed:', error);
      }
    }
  }

  generateWeeks() {
    this.weeks = [];
    for (let week = 1; week <= this.maxWeeks; week++) {
      const weekInfo = this.getWeekInfo(week);
      this.weeks.push(weekInfo);
    }
  }

  getWeekInfo(weekNumber: number): WeekInfo {
    const startDate = this.getDateOfWeek(this.currentYear, weekNumber);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    const season = this.getSeason(startDate);
    return {
      weekNumber,
      startDate,
      endDate,
      season
    };
  }

  getDateOfWeek(year: number, week: number): Date {
    // ISO 8601 szabvány szerint a hét hétfőn kezdődik
    const firstJanuary = new Date(year, 0, 1);
    
    // Az első hétfő keresése az évben
    const firstMondayOfYear = new Date(firstJanuary);
    const dayOfWeek = firstJanuary.getDay(); // 0 = vasárnap, 1 = hétfő
    
    // Hány nappal kell eltolni, hogy hétfőt kapjunk
    const daysToFirstMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
    firstMondayOfYear.setDate(firstJanuary.getDate() + daysToFirstMonday);
    
    // Ha az első hétfő január 4 után van, akkor az előző év utolsó hetéhez tartozik
    if (firstMondayOfYear.getDate() > 4) {
      firstMondayOfYear.setDate(firstMondayOfYear.getDate() - 7);
    }
    
    // A kért hét hétfőjének kiszámítása
    const targetMonday = new Date(firstMondayOfYear);
    targetMonday.setDate(firstMondayOfYear.getDate() + (week - 1) * 7);
    
    return targetMonday;
  }

  getSeason(date: Date): 'spring' | 'summer' | 'autumn' | 'winter' {
    const month = date.getMonth() + 1; // 1-12
    
    // Tél: december, január, február
    if (month === 12 || month === 1 || month === 2) {
      return 'winter';
    }
    // Tavasz: március, április, május
    else if (month === 3 || month === 4 || month === 5) {
      return 'spring';
    }
    // Nyár: június, július, augusztus
    else if (month === 6 || month === 7 || month === 8) {
      return 'summer';
    }
    // Ősz: szeptember, október, november
    else {
      return 'autumn';
    }
  }

  getCurrentWeekNumber(): number {
    const now = new Date();
    
    // ISO 8601 hét számítás
    const year = now.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    
    // Első hétfő keresése az évben
    const firstMonday = new Date(startOfYear);
    const dayOfWeek = startOfYear.getDay(); // 0 = vasárnap, 1 = hétfő
    const daysToMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek); // Hány nap múlva van hétfő
    firstMonday.setDate(startOfYear.getDate() + daysToMonday);
    
    // Ha az első hétfő január 4 után van, akkor az előző év utolsó hetéhez tartozik
    if (firstMonday.getDate() > 4) {
      firstMonday.setDate(firstMonday.getDate() - 7);
    }
    
    // Hét számítás
    const diffTime = now.getTime() - firstMonday.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const weekNumber = Math.floor(diffDays / 7) + 1;
    
    return Math.max(1, Math.min(52, weekNumber));
  }

  onWeekChange() {
    const selectedWeek = this.weeks[this.currentWeek - 1];
    this.weekChanged.emit(selectedWeek);
  }

  onExportClick(): void {
    this.exportRequested.emit();
  }

  onSliderInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.currentWeek = parseInt(target.value, 10);
    this.sliderInput$.next();
  }

  getCurrentWeekInfo(): WeekInfo {
    return this.weeks[this.currentWeek - 1] || this.getWeekInfo(1);
  }

  getSeasonIcon(season: string): string {
    switch (season) {
      case 'spring': return 'local_florist';
      case 'summer': return 'wb_sunny';
      case 'autumn': return 'thermostat';
      case 'winter': return 'ac_unit';
      default: return 'calendar_today';
    }
  }

  getSeasonName(season: string): string {
    // Az aktuális hónap nevét adja vissza az évszak helyett
    const currentWeek = this.getCurrentWeekInfo();
    return currentWeek.startDate.toLocaleDateString('hu-HU', { month: 'long' });
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('hu-HU', { 
      month: 'short', 
      day: 'numeric' 
    });
  }

  removeFocus(event: any) {
    try {
      // Eltávolítja a focust az elemről, amikor felengeded a kurzort
      if (event && event.target) {
        event.target.blur();
      }
      
      // Az input elemre is alkalmazni kell
      if (event && event.target) {
        const input = event.target.querySelector('input');
        if (input) {
          input.blur();
        }
      }
      
      // Ripple effektek újbóli eltávolítása
      setTimeout(() => {
        this.removeRippleEffects();
      }, 50);
    } catch (error) {
      // Csendes hiba kezelés
      console.debug('Focus removal failed:', error);
    }
  }
}
